import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, map, Observable, take } from 'rxjs';
import { AutoFocusTransitionDirective } from '../../directives/auto-focus-transition.directive';
import { NameSanitizerDirective } from '../../directives/name-sanitizer.directive';
import { PanFormatterDirective } from '../../directives/pan-formatter.directive';
import { TPSError } from '../../services/api/api.models';
import { ErrorService } from '../../services/error/error.service';
import { ExternalDataService } from '../../services/external-data/external-data.service';
import { PanTokenizationService } from '../../services/pan-tokenization/pan-tokenization.service';
import { EXPIRATION_DATE_INVALID, expirationDateValidator } from '../../validators/expiration-date-validator';
import { luhnValidator } from '../../validators/luhn-validators';
import { maxLengthWithoutSpacesValidator } from '../../validators/max-length-without-spaces-validator';
import { minLengthWithoutSpacesValidator } from '../../validators/min-length-without-spaces-validator';
import { monthFormatValidator } from '../../validators/month-format-validator';

type FormValues = Partial<{
  pan: string | null;
  prn: string | null;
  expDateYear: string | null;
  expDateMonth: string | null;
  cardOwnerName: string | null;
}>;

type Tab = 'PAN' | 'PRN';

@Component({
  selector: 'app-pan-tokenization-prn',
  imports: [ReactiveFormsModule, CommonModule, TranslateModule, AutoFocusTransitionDirective, NameSanitizerDirective, PanFormatterDirective],
  templateUrl: './pan-tokenization-prn.component.html',
  styleUrl: './pan-tokenization-prn.component.scss',
})
export class PanTokenizationPrnComponent implements OnInit {
  public form = new FormGroup(
    {
      pan: new FormControl('', [
        Validators.required,
        luhnValidator(),
        minLengthWithoutSpacesValidator(13),
        maxLengthWithoutSpacesValidator(19),
      ]),
      expDateMonth: new FormControl('', [Validators.required, monthFormatValidator()]),
      expDateYear: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{2}$/)]),
      cardOwnerName: new FormControl('', Validators.required),
      prn: new FormControl('', [Validators.required, luhnValidator(), Validators.pattern(/^\d{11}$/)]),
    },
    {
      updateOn: 'change',
    }
  );

  public fc = this.form.controls;

  public panCanBeInError = false;
  public expirationCanBeInError = false;
  public nameCanBeInError = false;
  public prnCanBeInError = false;

  // Show errors
  get showPanError() {
    return this.fc.pan.invalid && this.panCanBeInError;
  }
  get showExpirationError() {
    return (this.fc.expDateMonth.invalid || this.fc.expDateYear.invalid) && this.expirationCanBeInError;
  }
  get showNameError() {
    return this.fc.cardOwnerName.invalid && this.nameCanBeInError;
  }
  get showPrnError() {
    return this.fc.prn.invalid && this.prnCanBeInError;
  }

  get expirationDateInPast() {
    return (
      (this.fc.expDateYear.dirty || this.fc.expDateYear.touched) &&
      (this.fc.expDateMonth.dirty || this.fc.expDateMonth.touched) &&
      this.form.hasError(EXPIRATION_DATE_INVALID)
    );
  }

  // States
  public $activeTab = new BehaviorSubject<Tab>('PAN');
  public $isProcessedOrProcessing: Observable<boolean>;
  public $isDoing3DAuth: Observable<boolean>;
  public $panFormatting: Observable<boolean>;
  public $showExtendedFields: Observable<boolean>;
  public $showNameField: Observable<boolean>;
  public $placeholders: Observable<boolean>;
  public $permissiveSubmitButton: Observable<boolean>;
  public $tpsErrors: Observable<TPSError[]>;

  constructor(
    private panTokenizationService: PanTokenizationService,
    private externalDataService: ExternalDataService,
    private errorService: ErrorService
  ) {
    this.$isProcessedOrProcessing = this.panTokenizationService.$isProcessingOrProcessed;
    this.$isDoing3DAuth = this.panTokenizationService.$isDoing3DAuth;
    this.$panFormatting = this.externalDataService.$panFormatting;
    this.$showExtendedFields = this.externalDataService.$useShortCardTokenForm.pipe(map((useShort) => !useShort));
    this.$showNameField = combineLatest([this.$showExtendedFields, this.externalDataService.$use3DSCardTokenForm]).pipe(
      map(([showExtended, use3DS]) => showExtended && use3DS)
    );
    this.$placeholders = this.externalDataService.$placeholders;
    this.$permissiveSubmitButton = this.externalDataService.$permissiveSubmitButton;
    this.$tpsErrors = this.errorService.$tpsErrors;

    this.fc.pan.valueChanges.subscribe(() => (this.panCanBeInError = false));
    this.fc.expDateMonth.valueChanges.subscribe(() => (this.expirationCanBeInError = false));
    this.fc.expDateYear.valueChanges.subscribe(() => (this.expirationCanBeInError = false));
    this.fc.cardOwnerName.valueChanges.subscribe(() => (this.nameCanBeInError = false));
    this.fc.prn.valueChanges.subscribe(() => (this.prnCanBeInError = false));
  }

  // unsubscribe on destroy?
  ngOnInit(): void {
    combineLatest([
      this.$activeTab,
      this.panTokenizationService.$isProcessingOrProcessed,
      this.externalDataService.$useShortCardTokenForm,
      this.externalDataService.$use3DSCardTokenForm,
    ]).subscribe(([activeTab, isProcessingOrProcessed, useShortCardTokenForm, use3DSCardTokenForm]) => {
      if (activeTab === 'PRN') {
        this.fc.prn.enable();

        this.fc.pan.disable();
        this.fc.expDateMonth.disable();
        this.fc.expDateYear.disable();
        this.fc.cardOwnerName.disable();

        return this.form.updateValueAndValidity();
      }

      this.fc.prn.disable();

      if (!isProcessingOrProcessed) {
        this.fc.pan.enable();
      } else {
        this.fc.pan.disable();
      }

      if (!isProcessingOrProcessed && !useShortCardTokenForm) {
        this.fc.expDateMonth.enable();
        this.fc.expDateYear.enable();
        this.form.setValidators(expirationDateValidator('expDateMonth', 'expDateYear'));
      } else {
        this.fc.expDateMonth.disable();
        this.fc.expDateYear.disable();
        this.form.clearValidators();
      }

      if (!isProcessingOrProcessed && !useShortCardTokenForm && use3DSCardTokenForm) {
        this.fc.cardOwnerName.enable();
      } else {
        this.fc.cardOwnerName.disable();
      }

      this.form.updateValueAndValidity();
    });
  }

  onSubmit() {
    this.panCanBeInError = true;
    this.expirationCanBeInError = true;
    this.nameCanBeInError = true;
    this.prnCanBeInError = true;

    if (this.form.invalid) {
      return;
    }

    this.panTokenizationService.$isProcessingOrProcessed.next(true);
    this.errorService.clearTpsErrors();
    if (this.$activeTab.getValue() === 'PRN') {
      this.submitPanTokenFormWithPrn(this.form.value);
    } else {
      combineLatest([this.externalDataService.$useShortCardTokenForm, this.externalDataService.$use3DSCardTokenForm])
        .pipe(take(1))
        .subscribe(([useShortCardTokenForm, use3DSCardTokenForm]) => {
          const formValue = { ...this.form.value, pan: this.form.value.pan?.replaceAll(' ', '') };
          if (!useShortCardTokenForm && use3DSCardTokenForm) {
            this.submitPanTokenFormWith3DS(formValue);
          } else {
            this.submitPanTokenFormWithCardNumber(formValue);
          }
        });
    }
  }

  private submitPanTokenFormWithCardNumber(formValues: FormValues) {
    if (formValues.pan && formValues.expDateYear && formValues.expDateMonth) {
      const expiryDate = this.panTokenizationService.compileExpiryDate(formValues.expDateYear, formValues.expDateMonth);
      this.panTokenizationService.sendPanTokenGenerationRequestWithCardNumber(formValues.pan, expiryDate);
    } else if (formValues.pan) {
      this.panTokenizationService.sendPanTokenGenerationRequestWithCardNumber(formValues.pan, null);
    }
  }

  private submitPanTokenFormWithPrn(formValues: FormValues) {
    if (formValues.prn) {
      this.panTokenizationService.sendPanTokenGenerationRequestWithPrn(formValues.prn, null);
    }
  }

  private submitPanTokenFormWith3DS(formValues: FormValues) {
    if (formValues.pan && formValues.expDateYear && formValues.expDateMonth && formValues.cardOwnerName) {
      this.panTokenizationService.sendPanTokenGenerationWith3DSecure(
        formValues.pan,
        formValues.expDateYear,
        formValues.expDateMonth,
        formValues.cardOwnerName
      );
    }
  }

  switchTab(tab: Tab) {
    this.form.reset();
    this.$activeTab.next(tab);
  }
}
